import { Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import { BackdropOpen } from "App";
import { ArrayMethodPlateRoleId } from "enums/ArrayMethodPlateRoleIds";
import { Methods } from "enums/Methods";
import {
  useGetArrayMethodLabwareTypesLazyQuery,
  useGetCrisprSampleTypesQuery,
  useGetExistingMethodReservationLazyQuery,
  useGetIntermediateAndDestinationPlateBarcodesMutation,
  useReserveMethodMutation,
} from "graphql/generated/schema-types";
import { GetUrl } from "helpers/get-url";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IntermediatePlatesContainer } from "../Nucleofection/plates/IntPlatesContainer";
import { NewWorklistPreview } from "../shared/components/NewWorklistPreview";
import {
  LabwareTypeInfo,
  NucleofectionStep,
  Worklist,
} from "../shared/interfaces";
import { getPlateTypeInfoByRoleId } from "../shared/WorklistHelpers";
import {
  getDestPlateCode,
  updateDestTransferStratIfNon96W,
} from "./NucleofectionToolHelpers";
import { CompoundPlateContainer } from "./plates/CompoundPlateContainer";
import { DestinationPlatesContainer } from "./plates/DestinationPlatesContainer";
import { NucleofectionPlatesContainer } from "./plates/NucleofectionPlatesContainer";
import { SelectableIntPlatesContainer } from "./plates/SelectableIntPlatesContainer";
import { SourcePlatesContainer } from "./plates/SourcePlatesContainer";
import { NucleofectionSidebar } from "./sidebar/NucleofectionSidebar";
import { NucleofectionToolActions } from "./state";
import { NucleofectionInternalAction } from "./state/action";
import {
  useHandleMethodSettingsUpdated,
  useSetWorkistPreviewOpen,
  useSetWorklist,
} from "./state/handlers";
import { useNucleofectionStateProvider } from "./state/StateProvider";

const StyledPlateDiv = styled.div`
  height: calc(100vh - 200px);
  display: grid;
  grid-template: auto / 1fr 2fr;
`;

const steps = [
  "Select Source and Compound Plates",
  "Source To Nuc Mapping",
  "Nuc To Int Mapping",
  "Select Int To Dest Mapping",
];

export const Nucleofection = () => {
  const dispatch = useAppReduxDispatch();
  const { nucleofectionInternalState, nucleofectionDispatch } =
    useNucleofectionStateProvider();
  const [getAcceptablePlateTypes, { data: acceptablePlateTypes }] =
    useGetArrayMethodLabwareTypesLazyQuery({ fetchPolicy: "network-only" });
  const { data: crisprSampleTypes } = useGetCrisprSampleTypesQuery({
    fetchPolicy: "network-only",
  });
  const updateMethodSettings = useHandleMethodSettingsUpdated();

  const [sourcePlateTypes, setSourcePlateTypes] = useState<LabwareTypeInfo[]>();
  const [compoundPlateTypes, setCompoundPlateTypes] =
    useState<LabwareTypeInfo[]>();
  const [nucPlateTypes, setNucPlateTypes] = useState<LabwareTypeInfo[]>();
  const [intPlateTypes, setIntPlateTypes] = useState<LabwareTypeInfo[]>();
  const [destPlateTypes, setDestPlateTypes] = useState<LabwareTypeInfo[]>();
  const [getIntAndDestBarcodes] =
    useGetIntermediateAndDestinationPlateBarcodesMutation();
  const methodSettings = nucleofectionInternalState.methodSettings;
  const setWorklist = useSetWorklist();
  const setWorklistPreviewOpen = useSetWorkistPreviewOpen();
  const [reserveMethod, { loading }] = useReserveMethodMutation();

  const sourcePlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.sourcePlateInfo
  );
  const compoundPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.compoundPlateInfo
  );
  const nucPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.nucPlateInfo
  );
  const intPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.intPlateInfo
  );
  const destPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.destPlateInfo
  );

  const [getExistingMethodReservation, { data: methodReservation }] =
    useGetExistingMethodReservationLazyQuery({
      fetchPolicy: "network-only",
    });

  const destPlateLabwareCodeSignature = destPlateInfo
    .map((destPlate) => destPlate.labwareTypeCode)
    .filter((labwareTypeCode) => labwareTypeCode !== "")
    .join(";");

  useEffect(() => {
    if (
      nucleofectionInternalState.methodSettings.arraySystemAutomationMethodId
    ) {
      getAcceptablePlateTypes({
        variables: {
          where: {
            arraySystemAutomationMethodId: {
              eq: nucleofectionInternalState.methodSettings
                .arraySystemAutomationMethodId,
            },
          },
        },
      });
    }
  }, [nucleofectionInternalState.methodSettings.arraySystemAutomationMethodId]);

  useEffect(() => {
    if (acceptablePlateTypes) {
      setSourcePlateTypes(
        getPlateTypeInfoByRoleId(
          acceptablePlateTypes,
          ArrayMethodPlateRoleId.Source
        )
      );
      setCompoundPlateTypes(
        getPlateTypeInfoByRoleId(
          acceptablePlateTypes,
          ArrayMethodPlateRoleId.Auxilliary
        )
      );
      setNucPlateTypes(
        getPlateTypeInfoByRoleId(
          acceptablePlateTypes,
          ArrayMethodPlateRoleId.Intermediate
        )
      );
      setIntPlateTypes(
        getPlateTypeInfoByRoleId(
          acceptablePlateTypes,
          ArrayMethodPlateRoleId.Intermediate2
        )
      );
      setDestPlateTypes(
        getPlateTypeInfoByRoleId(
          acceptablePlateTypes,
          ArrayMethodPlateRoleId.Destination
        )
      );
    }
  }, [acceptablePlateTypes]);

  useEffect(() => {
    //If uploaded worklist don't query DB
    if (nucleofectionInternalState.uploadedWorklist) {
      nucleofectionDispatch({
        type: NucleofectionInternalAction.SET_STATE,
        payload: {
          key: "uploadedWorklist",
          value: false,
        },
      });
      return;
    }

    const uniqueDestPlateTypes = [
      ...new Set(
        destPlateInfo
          .map((e) => e.labwareTypeCode)
          .filter((labware) => labware !== "")
      ),
    ];
    if (
      uniqueDestPlateTypes.length > 0 &&
      sourcePlateInfo[0].plateBarcode !== "" &&
      destPlateInfo[0].labwareTypeCode !== ""
    ) {
      const destCodes = destPlateInfo
        .map((destPlate) => getDestPlateCode(destPlate))
        .join(";");
      getIntAndDestBarcodes({
        variables: {
          sourcePlateBarcode: sourcePlateInfo[0].plateBarcode,
          nucPlateBarcode: nucPlateInfo[0].plateBarcode,
          destPlateCodes: destCodes,
          hasMultipleDestPlates: destPlateInfo.length > 1,
          hasMultipleDestPlateTypes: uniqueDestPlateTypes.length > 1,
        },
      }).then((res) => {
        const plates = res?.data?.intermediateAndDestinationPlateBarcodes;
        const intPlateBarcode = plates?.intermediatePlateBarcode;
        const destPlateBarcodes = plates?.destinationPlateBarcodes || [];

        // Update intermediate plate barcode
        if (intPlateBarcode) {
          dispatch(
            NucleofectionToolActions.UPDATE_INT_PLATEBARCODE({
              intPlateBarcode,
            })
          );
        }

        // Update destination plate barcodes
        if (!destPlateBarcodes?.length) return;

        for (let index = 0; index < destPlateBarcodes.length; index++) {
          const barcode = destPlateBarcodes[index];
          const labwareTypeCode = destPlateInfo[index].labwareTypeCode;
          if (!labwareTypeCode || !barcode) continue;

          dispatch(
            NucleofectionToolActions.UPDATE_DEST_PLATEBARCODE({
              index,
              plateBarcode: barcode,
            })
          );
        }
      });
    }
  }, [sourcePlateInfo, destPlateLabwareCodeSignature]);

  useEffect(() => {
    if (nucPlateInfo[0].plateBarcode === "") {
      nucleofectionDispatch({
        type: NucleofectionInternalAction.SET_NUCLEOFECTION_ALREADY_RESERVED,
        payload: { isNucPlateAlreadyReserved: false },
      });
    }
  }, [nucPlateInfo[0].plateBarcode]);

  const handleBack = () => {
    nucleofectionDispatch({
      type: NucleofectionInternalAction.SET_PREV_STEP,
    });
  };

  const handleNext = async () => {
    nucleofectionDispatch({
      type: NucleofectionInternalAction.SET_NEXT_STEP,
    });
  };

  useEffect(() => {
    BackdropOpen({
      open: loading,
      header: "Generating Worklist",
      subText: "Do not reload page",
      size: 60,
    });
  }, [loading]);

  const handleWorklistPreviewClose = (
    generateWorklist: boolean,
    worklist: Worklist[],
    reservationTime: Date
  ) => {
    const methodReservationInput = {
      methodId: Methods.Nucleofection,
      selectedSystem: methodSettings.selectedSystem,
      sourcePlateInfo: sourcePlateInfo.map((plate) => ({
        plateBarcode: plate.plateBarcode,
        plateType: plate.labwareTypeCode,
      })),
      destPlateInfo: destPlateInfo.map((plate: any) => ({
        plateBarcode: plate.plateBarcode,
        plateType: plate.labwareTypeCode,
      })),
      compoundPlateBarcode: compoundPlateInfo[0].plateBarcode,
      intPlateBarcode: nucPlateInfo[0].plateBarcode,
      intPlateBarcode2: intPlateInfo[0].plateBarcode,
      deadPlateBarcode: "",
      worklist: worklist,
      reservationTime,
    };
    if (generateWorklist) {
      reserveMethod({
        variables: {
          methodReservationInput: methodReservationInput,
        },
      }).then((res) => {
        // Can't download files via GraphQL
        window.location.href = `${GetUrl()}/api/Download/DownloadWorklist?methodReservationID=${
          res.data?.reserveMethod?.methodReservationId
        }`;
        getExistingMethodReservation({
          variables: {
            where: {
              isCompleted: { eq: false },
              methodReservationsPlateBarcodes: {
                some: { plateBarcode: { eq: sourcePlateInfo[0].plateBarcode } },
              },
            },
          },
        });
      });
    }
    setWorklistPreviewOpen(false);
  };

  useEffect(() => {
    updateDestTransferStratIfNon96W(
      destPlateInfo,
      methodSettings.destTransferStrategy,
      updateMethodSettings
    );
  }, [destPlateInfo]);

  return (
    <React.Fragment>
      <StyledPlateDiv>
        <NucleofectionSidebar
          compoundPlateInfo={compoundPlateTypes}
          destSampleTypes={crisprSampleTypes}
        />
        <Box sx={{ width: "95%", height: "100%" }}>
          <Stepper
            activeStep={nucleofectionInternalState.step}
            sx={{ width: "85%", margin: "0 auto" }}
          >
            {steps.map((label) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          <div>
            <div style={{ display: "flex" }}>
              {nucleofectionInternalState.step ===
              NucleofectionStep.SelectSourceAndCompound ? (
                <React.Fragment>
                  <SourcePlatesContainer sourcePlateInfo={sourcePlateTypes} />
                  <CompoundPlateContainer
                    compoundPlateInfo={compoundPlateTypes}
                  />
                </React.Fragment>
              ) : nucleofectionInternalState.step ===
                NucleofectionStep.SelectSourceToNucleofection ? (
                <React.Fragment>
                  <SourcePlatesContainer sourcePlateInfo={sourcePlateTypes} />
                  <NucleofectionPlatesContainer nucPlateInfo={nucPlateTypes} />
                </React.Fragment>
              ) : nucleofectionInternalState.step ===
                NucleofectionStep.SelectNucleofectionToIntermediate ? (
                <React.Fragment>
                  <NucleofectionPlatesContainer nucPlateInfo={nucPlateTypes} />
                  <IntermediatePlatesContainer intPlateInfo={intPlateTypes} />
                </React.Fragment>
              ) : nucleofectionInternalState.step ===
                NucleofectionStep.SelectIntermediateToDestination ? (
                <React.Fragment>
                  <SelectableIntPlatesContainer intPlateInfo={intPlateTypes} />
                  <DestinationPlatesContainer destPlateInfo={destPlateTypes} />
                </React.Fragment>
              ) : null}
            </div>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Button
                color="inherit"
                disabled={nucleofectionInternalState.step === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              {nucleofectionInternalState.step < steps.length - 1 ? (
                <Button onClick={handleNext}>Next</Button>
              ) : null}
            </Box>
          </div>
        </Box>
      </StyledPlateDiv>
      <NewWorklistPreview
        isOpen={nucleofectionInternalState.worklistPreviewOpen}
        worklist={nucleofectionInternalState.worklist}
        setWorklist={setWorklist}
        handleClose={handleWorklistPreviewClose}
      />
    </React.Fragment>
  );
};
